// src/pages/NewBreakdownPage.js - COMPLETE VERSION WITH CORRECT HEADINGS IMPLEMENTATION
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { resolveLineItemLinks, normKey } from '../utils/quantityLinks';
import {
  fetchMaterials,
  fetchMurCategories,
  updateMaterialData,
  API_BASE_URL,
  fetchMaterialsWithBreakdown,
  fetchRegions,
  saveRValue,
  uploadRValueDocument,
} from '../services/api';
import api from '../services/api';
import './PageLayout.css';
import QuantityCalculatorModal from './QuantityCalculatorModal';
import { evaluate } from 'mathjs';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { useRegion } from '../context/RegionContext';
import StyledDropdown from '../components/StyledDropdown';

/* ---------- Shared Success Modal ---------- */
const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">
            {icon || '✅'}
          </div>
          <h2>{title || 'Success!'}</h2>
        </div>
        <div className="success-modal-body">
          <p>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

/* ========= Module-level constants ========= */

const TYPE_BASE = {
  CO2: 'Co2',
  BIO: 'Co2-Biogenic',
  WEIGHT: 'Weight',
  RATE: 'Rate',
};

// ✅ R-Value Categories (same as MaterialEditorPage)
const RVALUE_CATEGORIES = [
  'Roof',
  'Wall (above grade)',
  'Wall (below grade)',
  'Floor',
  'Windows and doors',
  'Skylights'
];

const dsidToSuffix = (dsid) => {
  const map = {
    vquest: '',
    'vquest-au-1': '-AU',
    'vquest-us-1': '-US',
    'vquest-uk-ICE': '-UK',
    'vquest-irl-1': '-IRL',
    'vquest-midrise': '-Midrise',
  };
  return map[dsid] ?? '';
};

const normalizeRegionToDSID = (r) => {
  const map = {
    nz: 'vquest',
    au: 'vquest-au-1',
    us: 'vquest-us-1',
    irl: 'vquest-irl-1',
    uk: 'vquest-uk-ICE',
    mr: 'vquest-midrise',
    vquest: 'vquest',
    'vquest-au-1': 'vquest-au-1',
    'vquest-us-1': 'vquest-us-1',
    'vquest-irl-1': 'vquest-irl-1',
    'vquest-uk-ICE': 'vquest-uk-ICE',
    'vquest-midrise': 'vquest-midrise',
  };
  return map[r] || r || 'vquest';
};

const metricFromItem = (li) => {
  const m = String(li?.measurementId || li?.code || '').toLowerCase();
  if (m.includes('rate')) return 'cost';
  if (m.includes('biogenic')) return 'biogenic';
  if (m.includes('weight')) return 'weight';
  if (m.includes('co2')) return 'carbon';
  return 'other';
};

function NewBreakdownPage() {
  const { selectedRegion } = useRegion();

  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    icon: '✅',
  });
  const showModal = (title, message, icon = '✅') =>
    setModal({ open: true, title, message, icon });

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => {
      try {
        showModal('Notice', String(msg ?? ''), 'ℹ️');
      } catch {
        originalAlert(msg);
      }
    };
    return () => {
      window.alert = originalAlert;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [regions, setRegions] = useState([]);
  const regionName = useMemo(() => {
    const r = regions.find(x => x.id === selectedRegion);
    return r ? r.name : selectedRegion;
  }, [regions, selectedRegion]);

  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState('');

  // ✅ R-Values state - Complete with category
  const [rValue, setRValue] = useState('');
  const [rValueCategory, setRValueCategory] = useState('');
  const [editingRValueCategory, setEditingRValueCategory] = useState(false);
  const [rValueFiles, setRValueFiles] = useState([]);
  const [uploadingRValue, setUploadingRValue] = useState(false);
  const [dragActiveRValue, setDragActiveRValue] = useState(false);
  const [rValueModalOpen, setRValueModalOpen] = useState(false);
  const [rValueCalculatorRows, setRValueCalculatorRows] = useState([]);
  const rValueCalculatorRowsRef = useRef([]);

  const [userProfile] = useState({
    region: selectedRegion || 'vquest',
    regionId: selectedRegion || 'vquest'
  });

  const [carbonCategories, setCarbonCategories] = useState([]);
  const [biogenicCategories, setBiogenicCategories] = useState([]);
  const [weightCategories, setWeightCategories] = useState([]);
  const [costCategories, setCostCategories] = useState([]);

  const [carbonLineItems, setCarbonLineItems] = useState([]);
  const [biogenicLineItems, setBiogenicLineItems] = useState([]);
  const [weightLineItems, setWeightLineItems] = useState([]);
  const [costLineItems, setCostLineItems] = useState([]);

  const [editingLineItems, setEditingLineItems] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [activeLineItem, setActiveLineItem] = useState(null);
  const [rowHistory, setRowHistory] = useState({});

  const [showImportModal, setShowImportModal] = useState(false);
  const [requestList, setRequestList] = useState([]);
  const [selectedReqIds, setSelectedReqIds] = useState(new Set());
  const adminHeaders = {
    'X-User-Role': 'admin',
    'X-User-Id': 'admin-demo',
    'X-User-Email': 'admin@example.com',
  };

  const getMeasurementId = useCallback(
    (base) => {
      const dsid = normalizeRegionToDSID(selectedRegion);
      const suffix = dsidToSuffix(dsid);
      return `vquest-${base}${suffix}`;
    },
    [selectedRegion]
  );

  const fetchJSON = async (url, opts = {}) => {
    try {
      // Get Firebase token
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Merge auth headers with any existing headers
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-region': selectedRegion || 'vquest', // ✅ Add region header
        ...opts.headers
      };

      const res = await fetch(url, { ...opts, headers });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `HTTP ${res.status}`);
      }
      
      return res.json();
    } catch (error) {
      console.error('❌ Fetch error:', error);
      throw error;
    }
  };

const openImportFromUserRequests = async () => {
  try {
    // ✅ Use api instead of fetchJSON (triggers rate-limit modal on 429)
    const response = await api.get('/composite-requests/pending', {
      headers: adminHeaders
    });
    
    const list = response.data;
    
    // ✅ DEBUG: Log the actual data structure
    console.log('🔍 DEBUG: Composite requests data:', list);
    if (list.length > 0) {
      console.log('🔍 DEBUG: First request LineItemsData:', list[0].LineItemsData);
      console.log('🔍 DEBUG: Sample line items:', list[0].LineItemsData?.slice(0, 3).map(item => ({
        description: item.description,
        isHeading: item.isHeading,
        heading: item.heading,
        allKeys: Object.keys(item)
      })));
    }
    
    setRequestList(Array.isArray(list) ? list : []);
    setSelectedReqIds(new Set());
    setShowImportModal(true);
  } catch (e) {
    console.error('❌ Error loading requests:', e);
    showModal('Load Failed', `Failed to load user requests: ${e.message || e}`, '⚠️');
  }
};


  const toggleReq = (id) => {
    setSelectedReqIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const importSelectedRequests = () => {
    if (!selectedReqIds.size) {
      showModal('Select Requests', 'Select at least one request to import.', 'ℹ️');
      return;
    }
    const chosen = requestList.filter(r => selectedReqIds.has(r.RequestId));
    const allItems = chosen.flatMap(r => Array.isArray(r.LineItemsData) ? r.LineItemsData : []);

    console.log('🔍 Importing items:', allItems.map(item => ({
      description: item.description?.substring(0, 50),
      isHeading: item.isHeading,
      heading: item.heading,
      subHeading: item.subHeading || item.sub_heading,
      category: item.category,
      keys: Object.keys(item)
    })));

    const normalise = (v) =>
      String(v || '').trim().toLowerCase();

    const getCategoryName = (c) =>
      c?.Category || c?.category || '';

    const getDescriptionName = (c) =>
      c?.MeasurementUnitName ||
      c?.CostMeasurementName ||
      c?.QuantityUnitName ||
      c?.Name ||
      c?.Description ||
      '';

    const findCategoryForLineItem = (li, categories) => {
      const code = normalise(li.code || li.measurementUnitRateId || li.MeasurementUnitRateId);
      const desc = normalise(li.description || li.Description);

      const match = (categories || []).find(c => {
        const catCode = normalise(c.MeasurementUnitRateId || c.measurementUnitRateId);
        const catDesc = normalise(getDescriptionName(c));

        return (
          (code && catCode && code === catCode) ||
          (desc && catDesc && desc === catDesc)
        );
      });

      return getCategoryName(match);
    };

    const norm = (li) => {
      const metricType = metricFromItem(li);
      let derivedCategory = li.category || li.Category || '';
      
      if (!derivedCategory && li.description) {
        if (metricType === 'carbon') {
          derivedCategory = findCategoryForLineItem(li, carbonCategories);
        } else if (metricType === 'biogenic') {
          derivedCategory = findCategoryForLineItem(li, biogenicCategories);
        } else if (metricType === 'weight') {
          derivedCategory = findCategoryForLineItem(li, weightCategories);
        } else if (metricType === 'cost') {
          derivedCategory = findCategoryForLineItem(li, costCategories);
        }
      }

      const obj = {
        id:           uuidv4(),
        measurementId:li.measurementId || li.code || '',
        description:  li.description || '',
        category:     derivedCategory,
        code:         li.code || '',
        quantity:     Number(li.quantity || 0),
        unit:         li.unit || '',
        value:        Number(li.value || 0),
        total:        Number(li.total || 0),
        lineItemId:   uuidv4(),
        // ✅ Stable link key (id/lineItemId are ephemeral uuids regenerated each load).
        // Section-scoped so the same material description in Carbon/Biogenic/Weight stays
        // distinct. Survives reloads because it's derived from stable content.
        linkKey:      normKey(`${metricType}::${li.description || ''}`),
        isHeading:    !!li.isHeading,
        isSubHeading: !!li.isSubHeading,   // 🆕
        heading:      li.heading    || '',
        subHeading: li.subHeading || li.sub_heading || '', // 🆕
      };
      
      if (Array.isArray(li.line_item_rows) && li.line_item_rows.length) {
        setRowHistory(prev => ({ ...prev, [obj.id]: li.line_item_rows }));
      }
      console.log('🧩 Imported line item category check:', {
        metricType,
        description: li.description,
        code: li.code,
        incomingCategory: li.category || li.Category,
        derivedCategory,
      });
      return obj;
    };

    // ✅ NEW: Function to insert heading rows before groups
    const insertHeadingRows = (items) => {
      if (!items || items.length === 0) return [];
 
      const result         = [];
      let lastHeading      = null;
      let lastSubHeading   = null;
 
      for (const item of items) {
        // ── Heading ───────────────────────────────────────────────────────
        if (item.heading && item.heading !== lastHeading) {
          result.push({
            id:           uuidv4(),
            lineItemId:   uuidv4(),
            isHeading:    true,
            isSubHeading: false,
            heading:      item.heading,
            subHeading:   '',
            measurementId:'',
            description:  '',
            category:     '',
            code:         '',
            quantity:     0,
            unit:         '',
            value:        0,
            total:        0,
          });
          lastHeading    = item.heading;
          lastSubHeading = null; // reset on new heading
        }
 
        // ── Sub-heading 🆕 ─────────────────────────────────────────────────
        if (item.subHeading && item.subHeading.trim() !== '' && item.subHeading !== lastSubHeading) {
          result.push({
            id:           uuidv4(),
            lineItemId:   uuidv4(),
            isHeading:    false,
            isSubHeading: true,
            heading:      lastHeading || '',
            subHeading:   item.subHeading,
            measurementId:'',
            description:  '',
            category:     '',
            code:         '',
            quantity:     0,
            unit:         '',
            value:        0,
            total:        0,
          });
          lastSubHeading = item.subHeading;
        }
 
        result.push(item);
      }
 
      return result;
    };

    // Process items by metric type
    const itemsCarbon   = [];
    const itemsBiogenic = [];
    const itemsWeight   = [];
    const itemsCost     = [];

    for (const li of allItems) {
      // Skip if this item itself is a heading row (shouldn't happen in current data)
      if (li.isHeading) continue;
      
      const normalized = norm(li);
      const t = metricFromItem(li);
      
      if (t === 'carbon') {
        itemsCarbon.push(normalized);
      } else if (t === 'biogenic') {
        itemsBiogenic.push(normalized);
      } else if (t === 'weight') {
        itemsWeight.push(normalized);
      } else if (t === 'cost') {
        itemsCost.push(normalized);
      }
    }

    // ✅ Insert heading rows before setting state
    setCarbonLineItems(insertHeadingRows(itemsCarbon));
    setBiogenicLineItems(insertHeadingRows(itemsBiogenic));
    setWeightLineItems(insertHeadingRows(itemsWeight));
    setCostLineItems(insertHeadingRows(itemsCost));

    setShowImportModal(false);
    const totalRegular = itemsCarbon.length + itemsBiogenic.length + itemsWeight.length + itemsCost.length;
    const totalWithHeadings = 
      insertHeadingRows(itemsCarbon).length + 
      insertHeadingRows(itemsBiogenic).length + 
      insertHeadingRows(itemsWeight).length + 
      insertHeadingRows(itemsCost).length;
    
    console.log('✅ Imported:', {
      regularItems: totalRegular,
      withHeadings: totalWithHeadings,
      headingsAdded: totalWithHeadings - totalRegular
    });
    
    showModal('Imported', `Imported ${totalRegular} line item(s) with ${totalWithHeadings - totalRegular} heading(s) from ${selectedReqIds.size} request(s).`, '📥');
  };


  const loadMaterials = useCallback(async () => {
    const stamp = Date.now();
    loadMaterials._last = stamp;
    try {
      const response = await fetchMaterials(selectedRegion);
      if (loadMaterials._last !== stamp) return;
      const allMaterials = response.data || [];

      const withBDResp = await fetchMaterialsWithBreakdown(selectedRegion);
      if (loadMaterials._last !== stamp) return;
      const withBD = (withBDResp?.data || withBDResp || []).map(
        (m) => m.QuantityUnitId || m.quantityUnitId || m.id
      );
      const withBDSet = new Set(
        withBD.filter(Boolean).map((x) => String(x).trim().toLowerCase())
      );

      const newMaterials = allMaterials.filter(
        (m) => !withBDSet.has(String(m.Id).trim().toLowerCase())
      );

      setMaterials(allMaterials);
      setFilteredMaterials(newMaterials);
    } catch (error) {
      console.error('❌ Error fetching materials:', error);
      setMaterials([]); setFilteredMaterials([]);
      showModal('Load Failed', 'Could not load materials. Please try again.', '⚠️');
    }
  }, [selectedRegion]);

  const loadCategories = useCallback(async () => {
    const stamp = Date.now();
    loadCategories._last = stamp;
    try {
      const [carbon, biogenic, weight, cost] = await Promise.all([
        fetchMurCategories(getMeasurementId(TYPE_BASE.CO2)),
        fetchMurCategories(getMeasurementId(TYPE_BASE.BIO)),
        fetchMurCategories(getMeasurementId(TYPE_BASE.WEIGHT)),
        fetchMurCategories(getMeasurementId(TYPE_BASE.RATE)),
      ]);
      if (loadCategories._last !== stamp) return;
      setCarbonCategories(carbon.data || []);
      setBiogenicCategories(biogenic.data || []);
      setWeightCategories(weight.data || []);
      setCostCategories(cost.data || []);

      console.log('✅ Loaded region-specific categories for:', selectedRegion);
      console.log('- Carbon MID:', getMeasurementId(TYPE_BASE.CO2), '| Categories:', carbon.data?.length || 0);
      console.log('- Biogenic MID:', getMeasurementId(TYPE_BASE.BIO), '| Categories:', biogenic.data?.length || 0);
      console.log('- Weight MID:', getMeasurementId(TYPE_BASE.WEIGHT), '| Categories:', weight.data?.length || 0);
      console.log('- Cost MID:', getMeasurementId(TYPE_BASE.RATE), '| Categories:', cost.data?.length || 0);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCarbonCategories([]); setBiogenicCategories([]); setWeightCategories([]); setCostCategories([]);
      showModal('Load Failed', 'Could not load categories. Please try again.', '⚠️');
    }
  }, [getMeasurementId, selectedRegion]);

  useEffect(() => {
    (async () => {
      try {
        const rs = await fetchRegions();
        setRegions(rs);
      } catch (e) {
        console.error('Error loading regions', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedRegion) return;

    localStorage.setItem('selectedRegion', selectedRegion);

    setSelectedMaterial('');
    setSelectedMaterialId('');
    setSearchQuery('');
    setCarbonLineItems([]);
    setBiogenicLineItems([]);
    setWeightLineItems([]);
    setCostLineItems([]);
    setRowHistory({});
    setEditingLineItems({});
    setRValue('');
    setRValueFiles([]);
    setRValueCalculatorRows([]);
    setRValueCategory('');
    setEditingRValueCategory(false);
    rValueCalculatorRowsRef.current = [];

    loadMaterials();
    loadCategories();
  }, [selectedRegion, loadMaterials, loadCategories]);

// Around line 503 - Complete updated prefill loading
useEffect(() => {
  // Wait for categories to load first
  if (!carbonCategories.length || !biogenicCategories.length || 
      !weightCategories.length || !costCategories.length) {
    console.log('⏳ Waiting for categories to load...');
    return;
  }

  try {
    const raw = localStorage.getItem('newBreakdownPrefill');
    if (!raw) return;
    
    console.log('📥 Loading prefill data from localStorage');
    const p = JSON.parse(raw);
    
    console.log('🔍 Prefill data structure:', {
      carbon: p.carbon?.length || 0,
      biogenic: p.biogenic?.length || 0,
      weight: p.weight?.length || 0,
      cost: p.cost?.length || 0
    });
    
    // ✅ Updated normalize function with category lookup
    const normalize = (arr, categories) =>
      (Array.isArray(arr) ? arr : []).map((it) => {
        console.log('📝 Normalizing item:', {
          isHeading: it.isHeading,
          heading: it.heading,
          description: it.description?.substring(0, 30),
          existingCategory: it.category
        });
        
        // ✅ Derive category if missing
        let derivedCategory = it.category || '';
        if (!derivedCategory && it.description && categories) {
          const match = categories.find(c => c.MeasurementUnitName === it.description);
          if (match) {
            derivedCategory = match.Category;
            console.log('✅ Derived category:', derivedCategory, 'for', it.description?.substring(0, 30));
          } else {
            console.warn('⚠️ No category match for:', it.description);
          }
        }
        
        return {
          id:           it.id || it.lineItemId || uuidv4(),
          measurementId:it.measurementId || '',
          description:  it.description || '',
          category:     derivedCategory,
          code:         it.code || '',
          quantity:     Number(it.quantity || 0),
          unit:         it.unit || '',
          value:        Number(it.value || 0),
          total:        Number(it.total || 0),
          lineItemId:   it.lineItemId || it.id || uuidv4(),
          isHeading:    !!it.isHeading,
          isSubHeading: !!it.isSubHeading,   // 🆕
          heading:      it.heading    || '',
          subHeading:   it.subHeading || '',  // 🆕
        };
      });
    
    // ✅ Pass categories to normalize
    const carbonItems = normalize(p.carbon, carbonCategories);
    const biogenicItems = normalize(p.biogenic, biogenicCategories);
    const weightItems = normalize(p.weight, weightCategories);
    const costItems = normalize(p.cost, costCategories);
    
    console.log('✅ Normalized items:', {
      carbon: carbonItems.length,
      biogenic: biogenicItems.length,
      weight: weightItems.length,
      cost: costItems.length,
      carbonHeadings: carbonItems.filter(i => i.isHeading).length,
      biogenicHeadings: biogenicItems.filter(i => i.isHeading).length,
      weightHeadings: weightItems.filter(i => i.isHeading).length,
      costHeadings: costItems.filter(i => i.isHeading).length,
      carbonWithCategories: carbonItems.filter(i => !i.isHeading && i.category).length,
      biogenicWithCategories: biogenicItems.filter(i => !i.isHeading && i.category).length
    });
    
    // Set state with a small delay
    setTimeout(() => {
      setCarbonLineItems(carbonItems);
      setBiogenicLineItems(biogenicItems);
      setWeightLineItems(weightItems);
      setCostLineItems(costItems);
      
      console.log('✅ State updated with categories');
      
      // Load rowHistory if present
      if (p.rowHistory) {
        console.log('✅ Loading rowHistory:', Object.keys(p.rowHistory).length, 'entries');
        setRowHistory(p.rowHistory);
      }
      
      localStorage.removeItem('newBreakdownPrefill');
      console.log('✅ Prefill data loaded successfully');
    }, 100);
    
  } catch (error) {
    console.error('❌ Error loading prefill data:', error);
  }
}, [carbonCategories, biogenicCategories, weightCategories, costCategories]);




  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = materials.filter(
      (material) =>
        material.Id.toString().toLowerCase().includes(query) ||
        material.Name.toLowerCase().includes(query)
    );
    setFilteredMaterials(filtered);
  };

  // ✅ CORRECT: Add heading as a separate row with isHeading flag
  const addHeading = (setLineItems, lineItems) => {
    const newHeading = {
      id: uuidv4(),
      lineItemId: uuidv4(),
      isHeading: true,
      heading: 'New Heading',
      measurementId: '',
      description: '',
      category: '',
      code: '',
      quantity: 0,
      unit: '',
      value: 0,
      total: 0,
    };
    
    setLineItems((prev) => [...prev, newHeading]);
  };

  const addSubHeading = (setLineItems, lineItems) => {
    const newSubHeading = {
      id:           uuidv4(),
      lineItemId:   uuidv4(),
      isHeading:    false,
      isSubHeading: true,
      heading:      '',
      subHeading:   'New Sub-Heading',
      measurementId:'',
      description:  '',
      category:     '',
      code:         '',
      quantity:     0,
      unit:         '',
      value:        0,
      total:        0,
    };
    setLineItems((prev) => [...prev, newSubHeading]);
  };

  // ✅ CORRECT: Handle heading text change
  const handleHeadingChange = (index, value, setLineItems, lineItems) => {
    const updated = [...lineItems];
    if (updated[index]) {
      updated[index].heading = value;
      setLineItems(updated);
    }
  };

  const handleSubHeadingChange = (index, value, setLineItems, lineItems) => {
    const updated = [...lineItems];
    if (updated[index]) {
      updated[index].subHeading = value;
      setLineItems(updated);
    }
  };

  const uniqueCategoryOptions = (cats) => {
    const seen = new Set(); const out = [];
    for (const c of cats || []) {
      const cat = (c.Category || '').trim();
      if (!cat || seen.has(cat)) continue;
      seen.add(cat);
      out.push({ value: cat, label: cat });
    }
    return out;
  };

  const uniqueDescOptions = (cats, category) => {
    const seen = new Set(); const out = [];
    for (const c of cats || []) {
      if (category && c.Category !== category) continue;
      const n = c?.MeasurementUnitName;
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push({ value: n, label: n });
    }
    return out;
  };

  const addLineItem = (setLineItems, lineItems, measurementBase) => {
    const newItem = {
      id: uuidv4(),
      measurementId: getMeasurementId(measurementBase),
      category: '',
      description: '',
      code: '',
      quantity: 0,
      unit: '',
      value: 0,
      total: 0,
      lineItemId: uuidv4(),
      isHeading: false,
      heading: '',
    };
    setLineItems([...lineItems, newItem]);

    logAction(
      auth.currentUser?.uid || 'unknown',
      auth.currentUser?.email || 'unknown',
      'ADD_LINE_ITEM',
      newItem
    );
  };

const copyLineItems = async () => {
  try {
    console.log('=== COPY TO BIOGENIC & WEIGHT START ===');
    
    const carbonItems = carbonLineItems.filter(item => !item.isHeading);
    console.log(`Copying ${carbonItems.length} Carbon items to Biogenic and Weight`);

    if (carbonItems.length === 0) {
      showModal('No Items', 'No Carbon items to copy.', 'ℹ️');
      return;
    }

    // ✅ STEP 1: Create ID mapping from Carbon IDs to new Biogenic/Weight IDs
    const idMapping = {};
    carbonItems.forEach(item => {
      const carbonId = item.lineItemId || item.id;
      idMapping[carbonId] = {
        biogenic: uuidv4(),
        weight: uuidv4()
      };
    });

    console.log('📋 ID Mapping created:', Object.keys(idMapping).length, 'mappings');
    console.log('🔍 Sample mapping:', Object.entries(idMapping)[0]);

    // ✅ Links are keyed on stable section-scoped keys ("carbon::desc"). When copying a
    // Carbon row to Biogenic/Weight, a link that pointed at a Carbon sibling should now point
    // at that sibling's copy in the target section — i.e. just swap the section prefix,
    // keeping the description. Returns null for non section-scoped (legacy) keys, which can't
    // be safely retargeted (open-time auto-heal recovers those via the label instead).
    const remapSectionKey = (linkedSourceId, targetType) => {
      if (linkedSourceId == null) return null;
      const m = /^(carbon|biogenic|weight|cost)::(.*)$/i.exec(String(linkedSourceId));
      return m ? `${targetType}::${m[2]}` : null;
    };

    // ✅ STEP 2: Helper function to remap linkedSourceId in an item
    const remapLinkedSourceId = (item, targetType) => {
      const remappedLinkedSourceId = remapSectionKey(item.linkedSourceId, targetType);
      if (remappedLinkedSourceId) {
        console.log(`🔗 Remapping linkedSourceId: ${item.linkedSourceId} → ${remappedLinkedSourceId} (${targetType})`);
      }

      // Also check and remap linkedSourceId inside line_item_rows (calculator rows)
      let remappedRows = item.line_item_rows;
      if (item.line_item_rows) {
        try {
          const rows = typeof item.line_item_rows === 'string'
            ? JSON.parse(item.line_item_rows)
            : item.line_item_rows;

          const updatedRows = rows.map(row => {
            const newLinkId = remapSectionKey(row.linkedSourceId, targetType);
            if (newLinkId) {
              console.log(`  🔗 Row linkedSourceId: ${row.linkedSourceId} → ${newLinkId}`);
              return {
                ...row,
                linkedSourceId: newLinkId
              };
            }
            return row;
          });

          remappedRows = updatedRows;
        } catch (e) {
          console.warn('⚠️ Failed to parse/remap line_item_rows:', e);
        }
      }

      return {
        remappedLinkedSourceId,
        remappedRows
      };
    };

    // ✅ STEP 3: Create Biogenic copies with remapped IDs
    const biogenicCopiedItems = carbonItems.map(item => {
      const carbonId = item.lineItemId || item.id;
      const newBiogenicId = idMapping[carbonId].biogenic;
      
      const { remappedLinkedSourceId, remappedRows } = remapLinkedSourceId(item, 'biogenic');
      
      return {
        ...item,
        id: newBiogenicId,
        lineItemId: newBiogenicId,
        linkedSourceId: remappedLinkedSourceId, // ✅ Remapped
        line_item_rows: remappedRows, // ✅ Remapped rows
        originalId: item.id,
        originalLineItemId: item.lineItemId,
        total: 0
      };
    });

    // ✅ STEP 4: Create Weight copies with remapped IDs
    const weightCopiedItems = carbonItems.map(item => {
      const carbonId = item.lineItemId || item.id;
      const newWeightId = idMapping[carbonId].weight;
      
      const { remappedLinkedSourceId, remappedRows } = remapLinkedSourceId(item, 'weight');
      
      return {
        ...item,
        id: newWeightId,
        lineItemId: newWeightId,
        linkedSourceId: remappedLinkedSourceId, // ✅ Remapped
        line_item_rows: remappedRows, // ✅ Remapped rows
        originalId: item.id,
        originalLineItemId: item.lineItemId,
        total: 0
      };
    });

    console.log('Biogenic copied items:', biogenicCopiedItems.length);
    console.log('Weight copied items:', weightCopiedItems.length);

    // ✅ STEP 5: Populate fields with region-specific data
    const populateFields = async (items, targetType, categories) => {
      const regionMID = getMeasurementId(targetType);
      
      console.log(`Populating fields for ${targetType} with regionMID: ${regionMID}`);

      return items
        .map(item => {
          const matchingCategory = categories.find(cat => 
            cat.Category === item.category && 
            cat.MeasurementUnitName === item.description
          );
          
          if (matchingCategory) {
            return {
              ...item,
              measurementId: regionMID,
              code: matchingCategory.MeasurementUnitRateId,
              unit: matchingCategory.QuantityTypeName,
              value: matchingCategory.Value,
              total: parseFloat(matchingCategory.Value) * parseFloat(item.quantity || 0),
              measurement_type: regionMID
            };
          } else {
            console.warn(`⚠️ No ${targetType} mapping for: ${item.description} (Category: ${item.category})`);
            return null;
          }
        })
        .filter(item => item !== null);
    };

    const biogenicItems = await populateFields(biogenicCopiedItems, TYPE_BASE.BIO, biogenicCategories);
    const weightItems = await populateFields(weightCopiedItems, TYPE_BASE.WEIGHT, weightCategories);

    console.log('Biogenic items after population:', biogenicItems.length, 'items');
    console.log('Weight items after population:', weightItems.length, 'items');

    // ✅ Log any remapped linked sources
    console.log('🔗 Biogenic items with links:', 
      biogenicItems.filter(i => i.linkedSourceId).map(i => ({
        id: i.lineItemId,
        linkedTo: i.linkedSourceId
      }))
    );
    console.log('🔗 Weight items with links:', 
      weightItems.filter(i => i.linkedSourceId).map(i => ({
        id: i.lineItemId,
        linkedTo: i.linkedSourceId
      }))
    );

    // Clear existing items
    setBiogenicLineItems([]);
    setWeightLineItems([]);

    // Clear old rowHistory
    setRowHistory(prev => {
      const newHistory = { ...prev };
      Object.keys(newHistory).forEach(key => {
        const item = biogenicLineItems.find(i => i.lineItemId === key) || 
                    weightLineItems.find(i => i.lineItemId === key);
        if (item) {
          delete newHistory[key];
        }
      });
      return newHistory;
    });

    // Set new items
    setBiogenicLineItems(biogenicItems);
    setWeightLineItems(weightItems);

    // ✅ STEP 6: Copy rowHistory with remapped linkedSourceId
    setRowHistory(prev => {
      const newHistory = { ...prev };
      
      biogenicItems.forEach(item => {
        const originalItem = carbonItems.find(c => 
          (c.lineItemId === item.originalLineItemId) || (c.id === item.originalId)
        );
        
        if (originalItem && prev[originalItem.lineItemId]) {
          // Remap linkedSourceId in history rows (swap section prefix: carbon → biogenic)
          const remappedHistory = prev[originalItem.lineItemId].map(row => {
            const newLinkId = remapSectionKey(row.linkedSourceId, 'biogenic');
            return newLinkId ? { ...row, linkedSourceId: newLinkId } : row;
          });

          newHistory[item.lineItemId] = remappedHistory;
          console.log(`✅ Copied and remapped history to Biogenic item ${item.lineItemId}`);
        }
      });
      
      weightItems.forEach(item => {
        const originalItem = carbonItems.find(c => 
          (c.lineItemId === item.originalLineItemId) || (c.id === item.originalId)
        );
        
        if (originalItem && prev[originalItem.lineItemId]) {
          // Remap linkedSourceId in history rows (swap section prefix: carbon → weight)
          const remappedHistory = prev[originalItem.lineItemId].map(row => {
            const newLinkId = remapSectionKey(row.linkedSourceId, 'weight');
            return newLinkId ? { ...row, linkedSourceId: newLinkId } : row;
          });

          newHistory[item.lineItemId] = remappedHistory;
          console.log(`✅ Copied and remapped history to Weight item ${item.lineItemId}`);
        }
      });
      
      return newHistory;
    });

    console.log('=== COPY COMPLETE ===');
    console.log(`Biogenic: ${biogenicItems.length} items with IDs:`, biogenicItems.map(i => i.lineItemId));
    console.log(`Weight: ${weightItems.length} items with IDs:`, weightItems.map(i => i.lineItemId));
    
    const skippedBiogenic = carbonItems.length - biogenicItems.length;
    const skippedWeight = carbonItems.length - weightItems.length;

    if (skippedBiogenic > 0 || skippedWeight > 0) {
      showModal(
        'Partial Copy',
        `Copied ${biogenicItems.length} items to Biogenic and ${weightItems.length} items to Weight.\n\n` +
        `⚠️ Skipped ${skippedBiogenic} Biogenic and ${skippedWeight} Weight items because they don't have valid category mappings in those regions.\n\n` +
        `To copy these items, you need to add the corresponding entries in the Biogenic/Weight category databases.`,
        '⚠️'
      );
    } else {
      showModal('Success', `Successfully copied ${carbonItems.length} items to Biogenic and Weight sections with linked cells preserved.`, '✅');
    }

  } catch (error) {
    console.error('Error copying line items:', error);
    showModal('Error', `Error copying items: ${error.message}`, '⚠️');
  }
};





  const handleLineItemCategoryChange = (index, category, setLineItems, lineItems, cats) => {
    const updated = [...lineItems];
    const item = updated[index] || {};

    let keep = false;
    if (item.description) {
      const rec = (cats || []).find(c => c.Category === category && c.MeasurementUnitName === item.description);
      keep = !!rec;
    }

    updated[index] = {
      ...item,
      category: category || '',
      ...(keep ? {} : {
        description: '',
        code: '',
        unit: '',
        value: 0,
        total: 0,
      }),
    };
    setLineItems(updated);
  };

  const handleLineItemChange = (index, field, value, setLineItems, lineItems, categories) => {
    const updated = [...lineItems];
    const prevValue = { ...updated[index] };

    if (field === 'quantity') {
      try {
        let q = parseFloat(value);
        if (String(value).startsWith('=')) q = evaluate(String(value).slice(1));
        updated[index][field] = q || 0;
        updated[index].total = parseFloat(updated[index].value || 0) * (q || 0);
      } catch {
        updated[index][field] = 0;
        updated[index].total = 0;
      }
    } else if (field === 'description') {
      updated[index][field] = value;
      const c = categories.find((cat) => cat.MeasurementUnitName === value);
      if (c) {
        updated[index].code = c.MeasurementUnitRateId;
        updated[index].value = c.Value;
        updated[index].unit = c.QuantityTypeName;
        updated[index].measurementId = c.MeasurementId || updated[index].measurementId;
        updated[index].category = c.Category || updated[index].category || '';
        updated[index].total = parseFloat(c.Value || 0) * parseFloat(updated[index].quantity || 0);
      }
    } else {
      updated[index][field] = value;
    }

    setLineItems(updated);
    logAction(auth.currentUser?.uid || 'unknown', auth.currentUser?.email || 'unknown', 'UPDATE_LINE_ITEM',
      { previousValue: prevValue, updatedValue: updated[index] });
  };

  const openRValueCalculatorModal = () => {
    setRValueModalOpen(true);
  };

  const saveRValueFromModal = async (calculatedValue, calculatorRows) => {
    try {
      console.log('🔍 saveRValueFromModal called with:', {
        calculatedValue,
        calculatorRows,
        calculatorRowsLength: calculatorRows?.length
      });

      const rowsToStore = calculatorRows || [];
      setRValueCalculatorRows(rowsToStore);
      rValueCalculatorRowsRef.current = rowsToStore;
      setRValue(calculatedValue);
      
      setRValueModalOpen(false);
      showModal('R-Value Set', `R-Value saved and ready for material insertion.`, '✅');
      
    } catch (error) {
      console.error('Error in saveRValueFromModal:', error);
      showModal('Error', 'Error setting R-Value: ' + error.message, '⚠️');
    }
  };

  const handleRValueDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveRValue(true);
    } else if (e.type === "dragleave") {
      setDragActiveRValue(false);
    }
  };

  const handleRValueDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveRValue(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleRValueFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleRValueFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleRValueFiles(Array.from(e.target.files));
    }
  };

  const handleRValueFiles = async (files) => {
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        showModal('File Too Large', `File ${file.name} is too large. Maximum size is 10MB.`, '⚠️');
        continue;
      }

      const newFile = {
        id: `rvalue_temp_${Date.now()}_${Math.random()}`,
        name: file.name,
        size: file.size,
        file: file,
        isTemp: true
      };
      
      setRValueFiles(prev => [...prev, newFile]);
      showModal('File Added', `File ${file.name} added. It will be uploaded when the material is saved.`, '📁');
    }
  };

  const removeRValueFile = (fileId) => {
    setRValueFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const saveQuantityFromModal = (calculatedQuantity, updatedRows) => {
    const { index, setLineItems, lineItems } = activeLineItem;
    const updated = [...lineItems];
    updated[index].quantity = calculatedQuantity;
    updated[index].total = parseFloat(updated[index].value || 0) * parseFloat(calculatedQuantity || 0);

    // ✅ FIX: Use lineItemId (database ID) instead of id (frontend ID)
    const lineItemId = lineItems[index].lineItemId;

    // ✅ Live links: propagate this item's new quantity to same-section rows linked to it.
    const resolvedHistory = { ...rowHistory, [lineItemId]: updatedRows };
    resolveLineItemLinks(updated, {
      rowsOf: (item) => resolvedHistory[item.lineItemId],
      setRows: (item, rows) => { resolvedHistory[item.lineItemId] = rows; },
      setQuantity: (item, qty) => {
        item.quantity = qty;
        item.total = parseFloat(item.value || 0) * qty;
      },
    });

    setRowHistory(resolvedHistory);
    setLineItems(updated);
    setModalOpen(false);
  };

  const openCalculatorModal = (index, setLineItems, lineItems) => {
    // ✅ Gather ALL line items from all sections for linking
    // ✅ Stamp a stable, section-scoped linkKey on every link candidate so the id stored at
    // link time matches what resolves on reload. normKey lowercases, so 'Carbon' here unifies
    // with the 'carbon' the import builder derives from metricFromItem.
    const withKey = (item, section) => ({
      ...item,
      sectionType: section,
      linkKey: item.linkKey || normKey(`${section}::${item.description || ''}`),
    });
    const allAvailableLineItems = [
      ...carbonLineItems.map(item => withKey(item, 'Carbon')),
      ...biogenicLineItems.map(item => withKey(item, 'Biogenic')),
      ...weightLineItems.map(item => withKey(item, 'Weight')),
      ...costLineItems.map(item => withKey(item, 'Cost'))
    ].filter(item => !item.isHeading && item.quantity > 0); // Only items with quantities

    setActiveLineItem({ 
      index, 
      setLineItems, 
      lineItems,
      allLineItems: allAvailableLineItems, // ✅ Add this
      sectionType: getSectionType(lineItems) // ✅ Add this helper
    });
    setModalOpen(true);
  };

  // ✅ Add helper function to determine section type
  const getSectionType = (lineItems) => {
    if (lineItems === carbonLineItems) return 'Carbon';
    if (lineItems === biogenicLineItems) return 'Biogenic';
    if (lineItems === weightLineItems) return 'Weight';
    if (lineItems === costLineItems) return 'Cost';
    return 'Unknown';
  };


  const toggleEdit = (index) => {
    setEditingLineItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const deleteLineItem = (index, setLineItems, lineItems) => {
    const updated = [...lineItems];
    const removed = updated.splice(index, 1)[0];
    setLineItems(updated);
    setEditingLineItems(prev => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
    setRowHistory(prev => {
      const n = { ...prev };
      delete n[removed?.id];
      return n;
    });
  };

  // ✅ CORRECT: renderLineItems with heading rows (NO insertHeadingRowsIfNeeded)
  const renderLineItems = (lineItems, setLineItems, categories, measurementBase) => {
    // Drag-and-drop handlers
    const handleDragStart = (e, index) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', index.toString());
      e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      return false;
    };

    const handleDrop = (e, dropIndex) => {
      e.stopPropagation();
      e.preventDefault();

      const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
      
      if (dragIndex === dropIndex) return;

      const updated = [...lineItems];
      const [draggedItem] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, draggedItem);

      // ✅ Update heading associations after reorder
      let currentHeading    = null;
      let currentSubHeading = null;
      updated.forEach((item, idx) => {
        if (item.isHeading) {
          currentHeading    = item.heading;
          currentSubHeading = null;
        } else if (item.isSubHeading) {
          currentSubHeading = item.subHeading;
        } else {
          updated[idx].heading    = currentHeading;
          updated[idx].subHeading = currentSubHeading;
        }
      });
      
      setLineItems([...updated]);
      
      console.log('🔄 Reordered items - moved from', dragIndex, 'to', dropIndex);
    };

    return (
      <>
        <div className="line-items-header">
          <span>⋮⋮</span>
          <span>MeasurementId</span>
          <span>Category</span>
          <span>Description</span>
          <span>Code</span>
          <span>Quantity</span>
          <span>Unit</span>
          <span>Value</span>
          <span>Total</span>
          <span>ID</span>
          <span>Actions</span>
        </div>

        {lineItems.map((item, index) => (
          <React.Fragment key={item.lineItemId}>
            {/* ── HEADING ROW ───────────────────────────────────────────── */}
            {item.isHeading && (
              <div
                className="heading-item"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <span className="drag-handle">⋮⋮</span>
                <input
                  type="text"
                  value={item.heading}
                  placeholder="Enter heading..."
                  onChange={(e) =>
                    handleHeadingChange(index, e.target.value, setLineItems, lineItems)
                  }
                />
                <button
                  className="btn btn--danger btn--small"
                  onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                >
                  Delete
                </button>
              </div>
            )}
 
            {/* ── SUB-HEADING ROW 🆕 ────────────────────────────────────── */}
            {item.isSubHeading && (
              <div
                className="subheading-item"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                style={{
                  display:    'flex',
                  alignItems: 'center',
                  gap:        8,
                  padding:    '6px 0 6px 32px',
                  background: '#f7f9fc',
                  borderLeft: '3px solid #a0bfdf',
                  marginBottom: 2,
                }}
              >
                <span className="drag-handle">⋮⋮</span>
                <span style={{ color: '#7a9cbf', fontSize: 16 }}>└</span>
                <input
                  className="input input--subheading"
                  type="text"
                  value={item.subHeading || ''}
                  placeholder="Enter sub-heading..."
                  onChange={(e) =>
                    handleSubHeadingChange(index, e.target.value, setLineItems, lineItems)
                  }
                  style={{ fontWeight: 600, fontSize: 13, color: '#2c5282', flex: 1 }}
                />
                <button
                  className="btn btn--danger btn--small"
                  onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                >
                  Delete
                </button>
              </div>
            )}
 
            {/* ── DATA ROW ──────────────────────────────────────────────── */}
            {!item.isHeading && !item.isSubHeading && (
              <div
                className="line-item"
                data-row-index={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <span className="drag-handle">⋮⋮</span>
                <input className="input input--readonly" type="text" value={item.measurementId} readOnly placeholder="MeasurementId" />
 
                {editingLineItems[index] ? (
                  <div style={{ minWidth: 200 }}>
                    <StyledDropdown
                      name={`cat-${item.id}`}
                      options={uniqueCategoryOptions(categories)}
                      value={item.category}
                      onChange={(val) =>
                        handleLineItemCategoryChange(index, val, setLineItems, lineItems, categories)
                      }
                      placeholder="Category"
                    />
                  </div>
                ) : (
                  <input className="input input--readonly" type="text" value={item.category || ''} readOnly />
                )}
 
                {editingLineItems[index] ? (
                  <div style={{ minWidth: 260 }}>
                    <StyledDropdown
                      name={`desc-${item.id}`}
                      options={uniqueDescOptions(categories, item.category)}
                      value={item.description}
                      onChange={(val) =>
                        handleLineItemChange(index, 'description', val, setLineItems, lineItems, categories)
                      }
                      placeholder="Select Description"
                    />
                  </div>
                ) : (
                  <input className="input input--readonly" type="text" value={item.description} readOnly />
                )}
 
                <input className="input input--readonly" type="text"   value={item.code}     readOnly placeholder="Code" />
                <input
                  className="input"
                  type="text"
                  value={item.quantity}
                  onChange={(e) =>
                    handleLineItemChange(index, 'quantity', e.target.value, setLineItems, lineItems, categories)
                  }
                  onFocus={() => openCalculatorModal(index, setLineItems, lineItems)}
                />
                <input className="input input--readonly" type="text"   value={item.unit}  readOnly placeholder="Unit" />
                <input className="input input--readonly" type="number" value={item.value} readOnly placeholder="Value" />
                <input className="input input--readonly" type="number" value={item.total} readOnly placeholder="Total" />
                <input className="input input--readonly" type="text"   value={item.lineItemId} readOnly placeholder="ID" />
 
                <div className="btn-group btn-group--row-actions">
                  <button
                    className="btn btn--primary btn--edit-save"
                    onClick={() => toggleEdit(index)}
                    aria-label={editingLineItems[index] ? 'Save row' : 'Edit row'}
                  >
                    {editingLineItems[index] ? 'Save' : 'Edit'}
                  </button>
                  <button
                    className="btn btn--danger btn--delete-row"
                    onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
 
          </React.Fragment>
        ))}
 
        {/* ── Action buttons ──────────────────────────────────────────────── */}
        <div className="section__actions">
          <button className="btn btn--success"
            onClick={() => addLineItem(setLineItems, lineItems, measurementBase)}>
            + Add Line Item
          </button>
          <button className="btn btn--indigo"
            onClick={() => addHeading(setLineItems, lineItems)}>
            + Add Heading
          </button>
          {/* 🆕 */}
          <button className="btn btn--teal"
            onClick={() => addSubHeading(setLineItems, lineItems)}>
            + Add Sub-Heading
          </button>
        </div>
      </>
    );
  };

  const handleMaterialChange = (e) => {
    const materialId = e.target.value;
    
    // ✅ Check if we're changing materials (not selecting for the first time)
    const isChangingMaterial = selectedMaterialId && selectedMaterialId !== materialId;
    
    setSelectedMaterial(materialId);
    const selected = materials.find((m) => m.Id === materialId);
    setSelectedMaterialId(selected?.Id || '');

    // ✅ Only clear data when actively switching between materials
    if (isChangingMaterial) {
      console.log('🔄 Changing material from', selectedMaterialId, 'to', materialId, '- clearing data');
      
      // Show warning before clearing
      const hasData = carbonLineItems.length > 0 || biogenicLineItems.length > 0 || 
                      weightLineItems.length > 0 || costLineItems.length > 0;
      
      if (hasData) {
        const confirmed = window.confirm(
          'Changing materials will clear all existing line items. Are you sure you want to continue?'
        );
        
        if (!confirmed) {
          // Revert material selection
          setSelectedMaterial(selectedMaterialId);
          return;
        }
      }
      
      // Clear all data
      setCarbonLineItems([]);
      setBiogenicLineItems([]);
      setWeightLineItems([]);
      setCostLineItems([]);
      setRValue('');
      setRValueFiles([]);
      setRValueCalculatorRows([]);
      setRValueCategory('');
      setEditingRValueCategory(false);
      rValueCalculatorRowsRef.current = [];
    } else {
      console.log('✅ Selecting material for first time - preserving existing line items');
    }
  };


  // ✅ R-Values section with Category dropdown (4-column layout)
  const renderRValuesSection = () => {
    const selectedMaterialData = materials.find(m => String(m.Id) === String(selectedMaterialId));
    
    return (
      <div className="section" style={{ marginBottom: '2rem', border: '2px solid #e3f2fd', borderRadius: '8px', background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)', padding: '1.5rem' }}>
        <h3 style={{ marginTop: 0, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 700 }}>
          📊 R-Values
        </h3>
        
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* R-Value Input Section with Category */}
          <div style={{ background: 'white', borderRadius: '6px', padding: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#2c3e50' }}>Quantity Unit</label>
                <input 
                  className="input input--readonly" 
                  type="text" 
                  value={selectedMaterialData?.Name || ''} 
                  readOnly 
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#2c3e50' }}>Category</label>
                {editingRValueCategory ? (
                  <div style={{ minWidth: 200 }}>
                    <StyledDropdown
                      name="rValueCategory"
                      options={RVALUE_CATEGORIES.map(cat => ({ value: cat, label: cat }))}
                      value={rValueCategory}
                      onChange={(val) => {
                        console.log('📝 R-Value Category changed to:', val);
                        setRValueCategory(val);
                        setEditingRValueCategory(false);
                      }}
                      placeholder="Select Category"
                    />
                  </div>
                ) : (
                  <input
                    className="input input--category"
                    type="text"
                    value={rValueCategory || 'Click to select category'}
                    onClick={() => {
                      console.log('🖱️ R-Value Category field clicked, current value:', rValueCategory);
                      setEditingRValueCategory(true);
                    }}
                    placeholder="Click to select category"
                    readOnly
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                )}
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#2c3e50' }}>R-Value</label>
                <input
                  className="input input--quantity"
                  type="text"
                  value={rValue}
                  onFocus={openRValueCalculatorModal}
                  placeholder="Click to enter R-Value"
                  readOnly
                  style={{ width: '100%' }}
                />
              </div>
              
              <div>
                {rValue && (
                  <button
                    className="btn btn--danger btn--small"
                    onClick={() => { 
                      setRValue(''); 
                      setRValueFiles([]); 
                      setRValueCalculatorRows([]);
                      setRValueCategory('');
                    }}
                    title="Clear R-Value"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div style={{ background: 'white', borderRadius: '6px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '1.1rem' }}>Supporting Documents</h4>
            <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
              Upload R-Value calculations, test reports, or other supporting documents (will be uploaded when material is saved)
            </p>
            
            <div 
              className={`file-upload-area clickable-upload ${dragActiveRValue ? 'drag-active' : ''}`}
              onDragEnter={handleRValueDrag}
              onDragLeave={handleRValueDrag}
              onDragOver={handleRValueDrag}
              onDrop={handleRValueDrop}
              onClick={() => document.getElementById('rValueFileInput').click()}
              style={{
                border: '2px dashed #ccc',
                borderRadius: '4px',
                padding: '2rem',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: dragActiveRValue ? '#f8f9ff' : '#f9f9f9'
              }}
            >
              <div className="upload-content">
                <div className="upload-icon" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                <p style={{ margin: '0 0 0.5rem 0' }}>Click here to upload R-Value documents or drag and drop</p>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>Supported formats: PDF, DOC, XLS, Images</span>
                <input
                  id="rValueFileInput"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleRValueFileInput}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                />
              </div>
            </div>

            {uploadingRValue && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <div className="spinner" style={{ display: 'inline-block', marginRight: '0.5rem' }}></div>
                <span>Uploading file...</span>
              </div>
            )}

            {rValueFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Files to Upload ({rValueFiles.length})</h4>
                {rValueFiles.map((file) => (
                  <div key={file.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '0.5rem' }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{file.name}</span>
                      <span style={{ marginLeft: '1rem', color: '#666', fontSize: '0.9rem' }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      {file.isTemp && <span style={{ marginLeft: '1rem', color: '#007bff', fontSize: '0.8rem' }}>(pending upload)</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRValueFile(file.id)}
                      style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ✅ CORRECT: insertMaterial with proper heading association
  const insertMaterial = async () => {
    try {
      if (!selectedMaterialId) {
        showModal('Select Material', 'Select a target material first.', 'ℹ️');
        return;
      }

      const currentCalculatorRows = rValueCalculatorRowsRef.current;

      console.log('🐛 DEBUG: R-Value state before saving:', {
        rValue,
        rValueCategory,
        rValueType: typeof rValue,
        rValueCalculatorRows,
        rValueCalculatorRowsType: typeof rValueCalculatorRows,
        rValueCalculatorRowsLength: rValueCalculatorRows?.length,
        rValueCalculatorRowsIsArray: Array.isArray(rValueCalculatorRows),
        rValueCalculatorRowsContent: JSON.stringify(rValueCalculatorRows)
      });

      const allLineItems = [
        ...carbonLineItems,
        ...biogenicLineItems,
        ...weightLineItems,
        ...costLineItems,
      ];

      console.log('🔍 Sample line item structure:', {
        firstItem: allLineItems[0],
        firstItemKeys: Object.keys(allLineItems[0]),
        firstItemCode: allLineItems[0]?.code,
        firstItemId: allLineItems[0]?.id,
        firstItemLineItemId: allLineItems[0]?.lineItemId
      });


      console.log('🔍 ALL LINE ITEMS BEFORE PROCESSING:', allLineItems.map((item, idx) => ({
        index: idx,
        isHeading: item.isHeading,
        heading: item.heading,
        id: item.id,
        description: item.description,
        measurementId: item.measurementId,  
        measurementType: item.measurementId
      })));

      // ✅ Build data with heading associations
      let currentHeading    = null;
      let currentSubHeading = null;
      const plainForUpdate  = [];
 
      allLineItems.forEach((item) => {
        if (item.isHeading) {
          currentHeading    = item.heading;
          currentSubHeading = null;
          return;
        }
        if (item.isSubHeading) {         // 🆕
          currentSubHeading = item.subHeading;
          return;
        }
 
        plainForUpdate.push({
          id:                   item.lineItemId,
          quantityUnitId:       selectedMaterialId,
          measurementUnitRateId:item.code,
          factor:               item.quantity,
          heading:              currentHeading,
          subHeading:           currentSubHeading,  // 🆕
          measurementType:      item.measurementId,
        });
      });

      console.log('📦 Data with heading associations:', plainForUpdate.map(item => ({
        id: item.id,
        factor: item.factor,
        heading: item.heading
      })));

      // Filter out heading info for original API (doesn't support headings)
      const originalFormattedData = plainForUpdate.map(({ id, quantityUnitId, measurementUnitRateId, factor }) => ({
        id,
        quantityUnitId,
        measurementUnitRateId,
        factor,
      }));

      // 1. Save material line items
      const originalResponse = await updateMaterialData({
        materialId: selectedMaterialId,
        lineItems: originalFormattedData,
      });

      if (!originalResponse?.data?.success) {
        showModal('Save Failed', 'Failed to insert material using updateMaterialData.', '⚠️');
        return;
      }
      console.log('🔍 DEBUG rowHistory keys and content:', {
        rowHistoryKeys: Object.keys(rowHistory),
        rowHistoryContent: rowHistory,
        plainForUpdateSample: plainForUpdate[0],
        firstMeasurementUnitRateId: plainForUpdate[0]?.measurementUnitRateId
      });

      // 2. ✅ Save line items with headings using api.post()
      const formattedDataWithRows = plainForUpdate.map((item, idx) => ({
        id:                       item.id,
        material_id:              selectedMaterialId,
        measurement_unit_rate_id: item.measurementUnitRateId || null,
        factor:                   item.factor,
        line_item_rows:           rowHistory[item.id] || [],
        headings:                 item.heading    || '',
        sub_heading:              item.subHeading || '',   // 🆕
        measurement_type:         item.measurementType || '',
        isHeading:                false,
        isSubHeading:             false,                   // 🆕
        sort_order:               idx,  
      }));

      console.log('📤 Sending line items with headings and calculator rows:', formattedDataWithRows.map(item => ({
        id: item.id,
        measurement_unit_rate_id: item.measurement_unit_rate_id,
        heading: item.headings,
        factor: item.factor,
        rowsCount: item.line_item_rows?.length || 0, // ✅ Add this to verify rows are included
        rows: item.line_item_rows // ✅ Log actual rows for debugging
      })));


      console.log('📤 Sending line items with headings:', formattedDataWithRows.map(item => ({
        id: item.id,
        heading: item.headings,
        factor: item.factor
      })));

      const lineItemsResponse = await api.post('/line-items', {
        materialId: selectedMaterialId,
        lineItems: formattedDataWithRows,
      });

      if (!lineItemsResponse.data?.success) {
        console.error('❌ Error inserting material with line_item_rows:', lineItemsResponse.data);
        showModal('Error', `Failed to save line item rows: ${lineItemsResponse.data?.message || 'Unknown error'}`, '⚠️');
        return;
      }

      console.log('✅ Line items with rows and headings saved:', lineItemsResponse.data);

      // 3. ✅ Save R-Value with category (only if provided)
      if (rValue && parseFloat(rValue) > 0) {
        try {
          console.log('🔍 About to save R-Value:', {
            materialId: selectedMaterialId,
            rValue: parseFloat(rValue),
            category: rValueCategory,
            calculatorRows: rValueCalculatorRows,
            calculatorRowsCount: rValueCalculatorRows.length,
            calculatorRowsIsArray: Array.isArray(rValueCalculatorRows)
          });

          const rValueData = {
            quantityUnitId: selectedMaterialId,
            rValue: parseFloat(rValue),
            calculatorRows: currentCalculatorRows,
            category: rValueCategory || '',
            region: userProfile?.region || 'vquest',
            regionId: userProfile?.regionId || 'vquest'
          };

          console.log('🔍 R-Value data being sent:', rValueData);

          const rValueResponse = await saveRValue(rValueData);
          console.log('✅ R-Value with calculator data saved:', rValueResponse);

          // 4. Upload R-Value files if any
          if (rValueFiles.length > 0 && rValueResponse.success) {
            setUploadingRValue(true);
            
            for (const file of rValueFiles.filter(f => f.isTemp)) {
              try {
                await uploadRValueDocument(rValueResponse.data?.id || rValueResponse.id, file.file);
                console.log('✅ Uploaded R-Value document:', file.name);
              } catch (uploadError) {
                console.error('Error uploading R-Value document:', uploadError);
                showModal('Upload Warning', `R-Value saved but failed to upload ${file.name}`, '⚠️');
              }
            }
            
            setUploadingRValue(false);
          }

        } catch (rValueError) {
          console.error('Error saving R-Value:', rValueError);
          showModal('R-Value Error', 'Material saved but failed to save R-Value. Please edit the material to add R-Value.', '⚠️');
        }
      } else {
        console.log('ℹ️ No R-Value provided, skipping R-Value save');
      }
      
      showModal('Material Saved', `Material and R-Value saved successfully for region ${regionName}!`, '🎉');

      logAction(auth.currentUser?.uid || 'unknown', auth.currentUser?.email || 'unknown', 'INSERT_MATERIAL_WITH_R_VALUE', {
        materialId: selectedMaterialId,
        lineItems: formattedDataWithRows,
        rValue: parseFloat(rValue),
        rValueCategory,
        rValueFilesCount: rValueFiles.length,
        regionId: selectedRegion,
        regionName,
      });

      // Clear form
      setCarbonLineItems([]);
      setBiogenicLineItems([]);
      setWeightLineItems([]);
      setCostLineItems([]);
      setRValue('');
      setRValueFiles([]);
      setRowHistory({});
      setRValueCalculatorRows([]);
      setRValueCategory('');
      setEditingRValueCategory(false);

    } catch (error) {
      console.error('Error inserting material:', error);
      showModal('Error', 'An error occurred while inserting the material.', '⚠️');
    }
  };

  const renderMaterialSearchAndDropdown = () => (
    <div className="form-container">
      <label htmlFor="materialSearch">Search Material</label>
      <input
        type="text"
        id="materialSearch"
        placeholder="Search by ID or Name"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      <label htmlFor="materialSelect">Select Material</label>
      <div style={{ width: 540 }}>
        <StyledDropdown
          name="materialSelect"
          options={filteredMaterials.map(m => ({
            value: m.Id,
            label: `${m.Name} (ID: ${m.Id})`,
          }))}
          value={selectedMaterial}
          onChange={(val) => handleMaterialChange({ target: { value: val } })}
          placeholder="Select Material"
        />
      </div>
      {selectedMaterialId && <span style={{ fontWeight: 'bold' }}>ID: {selectedMaterialId}</span>}
    </div>
  );

  useEffect(() => {
    const open = modalOpen || modal.open || showImportModal || rValueModalOpen;
    document.documentElement.classList.toggle('vq-modal-open', open);
    document.body.classList.toggle('vq-modal-open', open);
    return () => {
      document.documentElement.classList.remove('vq-modal-open');
      document.body.classList.remove('vq-modal-open');
    };
  }, [modalOpen, modal.open, showImportModal, rValueModalOpen]);

  return (
    <div className="rate-editor-page" key={selectedRegion}>
      <h1>Material Breakdown</h1>
      <div className="region-info" style={{marginBottom: 12}}>
        <span className="badge badge--region">Region: {regionName}</span>
        {selectedMaterialId && <span className="badge badge--material">Material ID: {selectedMaterialId}</span>}
      </div>

      <div className="top-bar">
        {renderMaterialSearchAndDropdown()}
        <button className="copy-line-items-button" onClick={copyLineItems}>Copy Line Items</button>
        <button className="copy-line-items-button" onClick={openImportFromUserRequests}>
          Insert from User Requests
        </button>
      </div>

      {selectedMaterialId && renderRValuesSection()}

      <div className="section">
        <div className="section">
          <h3>Carbon Line Items</h3>
          {renderLineItems(carbonLineItems, setCarbonLineItems, carbonCategories, TYPE_BASE.CO2)}
        </div>

        <div className="section">
          <h3>Biogenic Line Items</h3>
          {renderLineItems(biogenicLineItems, setBiogenicLineItems, biogenicCategories, TYPE_BASE.BIO)}
        </div>

        <div className="section">
          <h3>Weight Line Items</h3>
          {renderLineItems(weightLineItems, setWeightLineItems, weightCategories, TYPE_BASE.WEIGHT)}
        </div>

        <h3>Cost Line Items</h3>
        {renderLineItems(costLineItems, setCostLineItems, costCategories, TYPE_BASE.RATE)}
      </div>

      <QuantityCalculatorModal
        isOpen={rValueModalOpen}
        onClose={() => setRValueModalOpen(false)}
        onSave={saveRValueFromModal}
        initialRows={rValueCalculatorRows}
        title="R-Value Calculator"
      />

      {modalOpen && activeLineItem && (
        <QuantityCalculatorModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveQuantityFromModal}
          initialRows={rowHistory[activeLineItem.lineItems[activeLineItem.index]?.lineItemId] || []}
          lineItems={activeLineItem.allLineItems || []} // ✅ Pass all line items for linking
          sectionType={activeLineItem.sectionType || 'Carbon'} // ✅ Pass section type
          // ✅ Cycle-prevention identity uses the same stable linkKey as the links themselves.
          currentItemId={
            activeLineItem.lineItems[activeLineItem.index]?.linkKey ||
            normKey(`${activeLineItem.sectionType || 'Carbon'}::${activeLineItem.lineItems[activeLineItem.index]?.description || ''}`)
          }
          lineItemRowsById={(activeLineItem.allLineItems || []).reduce((acc, it) => {
            if (it.linkKey != null) acc[String(it.linkKey)] = rowHistory[it.lineItemId] || [];
            return acc;
          }, {})}
          activeLineItem={activeLineItem.lineItems[activeLineItem.index]} // ✅ Pass current item
          setRowHistory={setRowHistory} // ✅ Pass setRowHistory
          title="Quantity Calculator"
        />
      )}


      <div className="form-container" style={{ textAlign: 'center', marginTop: 20 }}>
        <button onClick={insertMaterial} className="insert-button">Insert Breakdown Data to Material</button>
      </div>

      {showImportModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowImportModal(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 10, width: 'min(900px, 95vw)', maxHeight: '85vh',
              overflow: 'auto', padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Insert from User Requests</h2>
              <button onClick={() => setShowImportModal(false)} style={{ background:'none', border:'none', fontSize:24, cursor:'pointer' }}>×</button>
            </div>

            {requestList.length === 0 ? (
              <div className="form-section" style={{ textAlign:'center' }}>
                <p>No pending composite requests found.</p>
              </div>
            ) : (
              <div style={{ display:'grid', gap:12 }}>
                {requestList.map(r => {
                  const md = r.MaterialData || {};
                  const name =
                    md.BaseMaterialName || md.OriginalMaterialName || md.SearchedMaterialName ||
                    md.RequestedName || md.Name || '(unnamed composite)';
                  const count = Array.isArray(r.LineItemsData) ? r.LineItemsData.length : 0;
                  return (
                    <div key={r.RequestId} className="form-section" style={{ margin:0, display:'grid', gridTemplateColumns:'auto 1fr auto', gap:12, alignItems:'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedReqIds.has(r.RequestId)}
                        onChange={() => toggleReq(r.RequestId)}
                      />
                      <div>
                        <div style={{ fontWeight:600 }}>{name}</div>
                        <div style={{ fontSize:13, color:'#555' }}>
                          {r.SubmittedByEmail} • {r.RegionName} • {new Date(r.SubmittedAt).toLocaleString()}
                        </div>
                        <div style={{ fontSize:13, color:'#1976d2' }}>{count} line items</div>
                      </div>
                      <button className="submit-request-button" onClick={() => { setSelectedReqIds(new Set([r.RequestId])); importSelectedRequests(); }}>
                        Insert This
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop:16, textAlign:'center', display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={importSelectedRequests} className="submit-request-button">Insert Selected</button>
              <button onClick={() => setShowImportModal(false)} style={{ background:'#666', color:'#fff', border:'none', borderRadius:6, padding:'10px 16px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
        icon={modal.icon}
      />
    </div>
  );
}

export default NewBreakdownPage;